import ClearIcon from "@mui/icons-material/Clear";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { inputLabelClasses } from "@mui/material/InputLabel";
import ListSubheader from "@mui/material/ListSubheader";
import MenuItem from "@mui/material/MenuItem";
import type { TextFieldProps } from "@mui/material/TextField";
import TextField from "@mui/material/TextField";
import { Messages } from "common/components/Elements/ExpandableMessages/ExpandableMessages";
import { hasContentForMessages } from "common/components/Elements/ExpandableMessages/utils.ts";
import InputAdormentLoading from "common/components/Elements/Form/InputAdormentLoading/InputAdormentLoading";
import InputDynamicLabel from "common/components/Elements/Form/InputDynamicLabel/InputDynamicLabel";
import type { TooltipHelp } from "common/components/Elements/Form/InputTooltipHelp/InputTooltipHelp";
import InputTooltipHelp from "common/components/Elements/Form/InputTooltipHelp/InputTooltipHelp";
import { arrayFlat } from "common/utilities/core/array";
import { generateRandomString } from "common/utilities/core/string";
import type * as I from "core/api/coreApiClient";
import type { ValueLabelOption } from "core/utilities/core.types";
import { inputStyles, inTableInputMarginTop } from "core/utilities/styles/input.styles";
import theme, { createStyles, sx, sxClassName } from "core/utilities/theme";
import I18n from "i18n/en-US";
import { useState } from "react";

export interface ValueLabelOptionsGroup {
  name: string;
  options: ValueLabelOption[];
}

const classes = createStyles({
  clearable: {
    paddingRight: theme.spacing(3),
  },
});

export interface ComponentProps {
  className?: string;
  value?: string;
  label?: string;
  title?: string;
  required?: boolean;
  disabled?: boolean;
  neverEmpty?: boolean;
  errorMsg?: I.Message[];
  onChange: (value?: string) => void;
  options?: ValueLabelOption[];
  optionsGroups?: ValueLabelOptionsGroup[];
  outlined?: boolean;
  e2eId?: string;
  longFormStyle?: boolean;
  clearable?: boolean;
  sideLabel?: boolean;
  tooltipHelp?: TooltipHelp;
  loading?: boolean;
  helpInfo?: I.Denotation;
  inTable?: boolean;
  afterFieldElement?: JSX.Element;
}

function InputSelect({
  value,
  options,
  optionsGroups,
  onChange,
  label,
  className,
  disabled,
  required,
  neverEmpty,
  errorMsg,
  outlined,
  e2eId,
  longFormStyle,
  clearable: clearableProp,
  sideLabel,
  tooltipHelp,
  loading,
  title,
  helpInfo,
  inTable,
  afterFieldElement,
}: ComponentProps) {
  const styles = inputStyles({
    longFormStyle,
    sideLabel,
    errorMsg,
    inTable,
    marginTop: inTableInputMarginTop.InputSelect,
  });

  const [id] = useState(() => generateRandomString());
  const [open, setOpen] = useState(false);

  const variantProps = outlined ? { variant: "outlined" as const } : { variant: "standard" as const };

  const clearable = clearableProp && !disabled && !(neverEmpty || !value);

  let endAdornment = (
    <InputAdornment position="end" style={{ marginRight: clearable ? theme.spacing(-3) : 0 }}>
      {clearable && (
        <Box mr={-1}>
          <IconButton onClick={() => onChange()} data-testid="reset" size="small">
            <ClearIcon style={{ fontSize: "16px" }} />
          </IconButton>
        </Box>
      )}
      {tooltipHelp && (
        <Box mr={-1}>
          <InputTooltipHelp tooltipHelp={tooltipHelp} />
        </Box>
      )}
      {!disabled && (
        <IconButton
          onClick={() => setOpen((prevState) => !prevState)}
          size="small"
          data-testid="toggle"
          aria-hidden="true"
          role="presentation"
          tabIndex={-1}
        >
          <KeyboardArrowDownIcon color="action" />
        </IconButton>
      )}
    </InputAdornment>
  );

  if (loading) {
    endAdornment = <InputAdormentLoading />;
  }

  const props: TextFieldProps = {
    sx: styles.root,
    select: true,
    SelectProps: {
      // Need to be truthy otherwise it will select a value when only one option is available
      displayEmpty: true,
      // eslint-disable-next-line react/no-unstable-nested-components, react/jsx-no-useless-fragment
      IconComponent: () => <></>,
      open,
      onOpen: () => setOpen(true),
      onClose: () => setOpen(false),
      "aria-label": label,
    },
    fullWidth: true,
    label: sideLabel ? undefined : label,
    title,
    value: value || "",
    onChange: (e) => {
      onChange(e.target.value);
    },
    margin: "dense",
    size: "small",
    className,
    required,
    disabled,
    error: errorMsg && errorMsg.length > 0,
    helperText: hasContentForMessages(errorMsg, helpInfo) ? (
      <Messages messages={errorMsg} denotation={helpInfo} />
    ) : undefined,
    InputProps: {
      id,
      sx: sx(styles.inputRoot, clearable && !loading ? classes.clearable : undefined),
      endAdornment,
    },
    InputLabelProps: {
      htmlFor: id,
      sx: {
        [sxClassName(inputLabelClasses.root)]: styles.labelRoot,
        [sxClassName(inputLabelClasses.disabled)]: styles.labelDisabled,
        [sxClassName(inputLabelClasses.shrink)]: styles.labelShrink,
      },
    },
    ...variantProps,
  };

  const optionsGroupsOptions = arrayFlat((optionsGroups || []).map((g) => g.options));
  const noOptionsAvailable = (options && options.length === 0) || (!options && optionsGroupsOptions.length === 0);
  const emptyOptionAvailable = (options && options.length > 0) || (!options && optionsGroupsOptions.length > 0);

  return (
    <InputDynamicLabel
      sideLabel={sideLabel}
      htmlFor={id}
      disabled={disabled}
      errorMsg={errorMsg}
      label={label || ""}
      required={required}
      testid="InputSelect"
      title={title}
    >
      <Box sx={{ display: "inline-flex", flexGrow: 1, width: "100%" }}>
        <TextField {...props} data-e2e-form-select={e2eId}>
          {noOptionsAvailable && (
            <MenuItem disabled dense>
              {I18n.Elements.Form.InputSelect.noOptions}
            </MenuItem>
          )}
          {emptyOptionAvailable && !neverEmpty && !required && (
            <MenuItem dense value="">
              <span style={{ visibility: "hidden" }}>Empty</span>
            </MenuItem>
          )}

          {options &&
            options.map((option) => (
              <MenuItem key={option.value} value={option.value} data-e2e-form-select-option={option.value} dense>
                {option.label}
              </MenuItem>
            ))}

          {optionsGroups &&
            optionsGroups.map((group) =>
              [
                group.name === "default"
                  ? ""
                  : [
                      <Divider key={`${group.name}divider`} aria-hidden="true" />,
                      <ListSubheader key={`${group.name}label`} aria-hidden="true">
                        {group.name}
                      </ListSubheader>,
                    ].flat(),
                group.options.map((option) => (
                  <MenuItem
                    key={group.name + option.value}
                    value={option.value}
                    data-e2e-form-select-option={option.value}
                    dense
                  >
                    {option.label}
                  </MenuItem>
                )),
              ].flat(),
            )}
        </TextField>
        {afterFieldElement}
      </Box>
    </InputDynamicLabel>
  );
}

export default InputSelect;
